import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { AssetCameraKeySchema, SiteIdCodec } from "@visioni/a3s-schema/codecs";
import { stringToInt } from "@visioni/a3s-codecs";
import { FEATURE_NAME } from "../feature-name";

/**
 * Patriot activations — the tickets an a3s-marked signal raised and an operator
 * attended — listed at whichever level of the fleet the caller is looking at.
 *
 * ## What a caller sees, and what it deliberately does not
 *
 * The routes serving these are support-only today, so the allowlist is not the
 * access control — it is the contract that lets the shape be widened to tenant
 * users without re-auditing every field Patriot happens to return.
 *
 * A Patriot activation carries far more than a customer should read: the
 * monitoring operator's name, the contact roster Patriot dialled (names, phone
 * numbers, addresses), Patriot-internal identifiers, and raw device payloads.
 * The schemas below are an **allowlist**, not a trim of the stored record — a
 * field reaches a customer only by being named here.
 *
 * Excluded on purpose:
 *
 * - `operatorName` / `operatorResponseTime` — identifies a Vision operator and
 *   measures them. Not the customer's business.
 * - `response` — the dialled contact roster. The customer's own contacts are
 *   theirs, but they belong to a3s's provisioning records, not to a replica of
 *   a third party's copy of them.
 * - `clientNo` / `clientName` — Patriot's own account identity. A customer
 *   knows their camera; the Patriot client number is a mapping detail.
 * - `rawData` / `media` on a signal, and `concurrencyFlag` / `typeNo` /
 *   `actionPlanId` — device payloads and internal plumbing.
 *
 * Named **activation** throughout, which is Patriot's own word for the record.
 * It is also the unambiguous one: `a3s-alarm-schedules` and the app already own
 * `alarm` for arming windows, down to an `AlarmDetail` of their own, so calling
 * this an alarm would have collided with a live concept in the same codebase.
 */

/**
 * One row of an activation list.
 *
 * **Patriot's fields keep Patriot's names**, `*DateTime` suffixes included;
 * a3s-owned fields are `a3s`-prefixed. The replica is a faithful copy, so a
 * second vocabulary for the same values is a translation layer somebody has to
 * keep correct forever.
 *
 * `pendingState` and `completedStatus` **co-exist** on one activation, so
 * neither is derivable from the other and both are carried.
 */
export const ActivationSummary = z.object({
  /** Patriot's ticket number — the reference an operator and a customer both quote. */
  ticketNo: z.string(),
  /** When Patriot created the activation, normalised to UTC. */
  createdDateTime: z.string(),
  /** Patriot's lifecycle state (`AttendPendingState`), while there is one. */
  pendingState: z.string().optional(),
  /** Patriot's terminal status (`AlarmCompleteStatus`: `COMPLETED`, `CANCELLED`, …), once there is one. */
  completedStatus: z.string().optional(),
  /** Operator-facing summary line, e.g. `Vehicle Detected`. */
  description: z.string().optional(),
  /** Patriot priority; lower is more urgent. */
  priority: z.number().optional(),
  /**
   * Where the a3s marker placed it. Present together or not at all, and carried
   * on every row because an unfiltered list spans the whole tenant — a row
   * saying only `camera-a` would not identify anything there.
   *
   * Prefixed although Patriot's `Activation` has no field of these names today:
   * Patriot uses `site` across `Client` for the *monitored premises*, which is
   * not an a3s site, so a future `Activation.site` would be a different thing
   * under the same name.
   */
  a3sSite: z.string().optional(),
  a3sAsset: z.string().optional(),
  a3sCamera: z.string().optional(),
});
export type ActivationSummary = z.output<typeof ActivationSummary>;

export const ListTenantActivationsResponse = z.object({
  items: z.array(ActivationSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListTenantActivationsResponse = z.output<typeof ListTenantActivationsResponse>;

/**
 * `site` and `camera` narrow the list the same way `since` / `until` do — they
 * are filters, not a route. Absent, the list spans the tenant.
 *
 * `limit` defaults rather than staying optional all the way down: left unset the
 * query carries no `Limit` and DynamoDB answers with a full 1MB page. `since` /
 * `until` are inclusive instants, in any offset, against the activation's raised
 * time.
 */
export const ListTenantActivationsQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }).optional(),
    until: z.iso.datetime({ offset: true }).optional(),
    site: SiteIdCodec.optional(),
    camera: AssetCameraKeySchema.optional(),
  })
  // The replica indexes a camera under its site, so there is no partition
  // holding one camera across every site. Rejected at the codec rather than
  // silently widening to the tenant, which would answer a question the caller
  // did not ask.
  .refine((q) => !(q.camera !== undefined && q.site === undefined), {
    message: "camera cannot be filtered without site",
    path: ["camera"],
  })
  .refine((q) => q.since === undefined || q.until === undefined || Date.parse(q.since) <= Date.parse(q.until), {
    message: "until cannot be before since",
    path: ["until"],
  });
export type ListTenantActivationsQueryParamsWire = z.input<typeof ListTenantActivationsQueryParams>;
export type ListTenantActivationsQueryParams = z.output<typeof ListTenantActivationsQueryParams>;

/**
 * ## Paths
 *
 * Everything Patriot-derived sits under a `patriot/` segment, so the replica's
 * surface is one subtree a reader can take in at once rather than routes mixed
 * in among a3s's own.
 *
 * ```
 * /features/monitoring/patriot/tenant/activations[?site=&camera=]
 * /features/monitoring/patriot/tenant/activations/{ticketNo}
 * /features/monitoring/patriot/fleet/activations
 * /features/monitoring/patriot/fleet/activations/{ticketNo}
 * ```
 *
 * The `tenant/` or `fleet/` segment names the scope a route reads, so the two
 * cannot be mistaken for each other in a log or a link.
 *
 * **Within the tenant scope, one list endpoint narrowed by query params
 * rather than a route per width.** The widths are the same list read at different depths of the same
 * hierarchy, so they share a response shape, a cursor and a client hook —
 * splitting them across routes only forced callers to choose a URL by which
 * filters they happened to hold. Keeping them together also makes the cache
 * key group them: every view shares `resource`, differing only in `query`, so
 * one invalidation covers all of them.
 *
 * All are declared `scope: "tenant"` and spell out their own tail. That is
 * not a claim about who may call them — all are support-only. It is because
 * the shared camera scope hard-codes `sites/{siteId}/cameras/…` directly after
 * the feature name, which leaves nowhere to put `patriot/`.
 */
export const tenantActivationsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/activations",
  scope: "tenant",
});

export const listTenantActivationsEndpoint = defineEndpoint(tenantActivationsResource, {
  responseSchema: ListTenantActivationsResponse,
  querySchema: ListTenantActivationsQueryParams,
});

/**
 * {@link ActivationSummary} plus the tenant it belongs to, since the fleet list
 * spans tenants. Absent, like every `a3s*` field, on an unattributed activation.
 */
export const FleetActivationSummary = ActivationSummary.extend({
  a3sTenant: z.string().optional(),
});
export type FleetActivationSummary = z.output<typeof FleetActivationSummary>;

export const ListFleetActivationsResponse = z.object({
  items: z.array(FleetActivationSummary),
  /**
   * Opaque resume token; `null` when the window is exhausted. Pass back
   * verbatim. A page can come back empty with a non-null cursor — the months
   * it walked held nothing — so page until this is `null`, not until a page is
   * empty.
   */
  cursor: z.string().nullable(),
});
export type ListFleetActivationsResponse = z.output<typeof ListFleetActivationsResponse>;

/**
 * Only a time window: the fleet index is bucketed by month and knows nothing of
 * sites or cameras, whose ids are tenant-scoped anyway.
 *
 * `since` is **required** — the index is walked month by month backwards, and
 * an open-ended read would walk into empty history with nothing to stop it.
 */
export const ListFleetActivationsQueryParams = z
  .object({
    limit: stringToInt.pipe(z.int().positive().max(100)).default(20),
    cursor: z.string().optional(),
    since: z.iso.datetime({ offset: true }),
    until: z.iso.datetime({ offset: true }).optional(),
  })
  .refine((q) => q.until === undefined || Date.parse(q.since) <= Date.parse(q.until), {
    message: "until cannot be before since",
    path: ["until"],
  });
export type ListFleetActivationsQueryParamsWire = z.input<typeof ListFleetActivationsQueryParams>;
export type ListFleetActivationsQueryParams = z.output<typeof ListFleetActivationsQueryParams>;

/**
 * Every activation in the replica, whichever tenant it belongs to — including
 * unattributed ones (Patriot's supervision alarms, other ingest paths), which
 * appear in no tenant list because the fleet reads Patriot's own month index.
 *
 * Under the caller's tenant in the path only because every client-API route is
 * — the gateway mounts the feature at `/tenants/{tenantId}/features/…`. The
 * tenant does not narrow the list; the support gate is the whole check.
 * `patriot/fleet/…` rather than a query flag on the tenant list, so a caller
 * can never widen a tenant read to the fleet by adding a parameter.
 */
export const fleetActivationsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/activations",
  scope: "tenant",
});

export const listFleetActivationsEndpoint = defineEndpoint(fleetActivationsResource, {
  responseSchema: ListFleetActivationsResponse,
  querySchema: ListFleetActivationsQueryParams,
});

/**
 * One signal on an activation — what the camera sent that Patriot acted on.
 *
 * **Patriot has a `signalId` of its own, and this is not it.** This is the id
 * from the a3s marker, which is what leads to the clip; Patriot's is an internal
 * integer the allowlist does not expose. A signal Patriot holds with no a3s
 * marker has no clip to reach and is omitted.
 */
export const ActivationSignal = z.object({
  a3sSignalId: z.string(),
  receivedDateTime: z.string(),
  description: z.string().optional(),
});
export type ActivationSignal = z.output<typeof ActivationSignal>;

/**
 * One operator note on an activation.
 *
 * **Free text written by a monitoring operator**, not composed for the customer.
 * It is surfaced because the activation's story is otherwise just timestamps, but it
 * is the one field here whose content nobody has reviewed — see the module
 * comment's allowlist rationale.
 *
 * Named in full because Patriot calls three unrelated fields `notes`; the type
 * is what disambiguates the one below. See `StoredOperatorNote` in
 * `client-api/activations/project.ts`.
 */
export const ActivationOperatorNote = z.object({
  noteId: z.string(),
  noteDateTime: z.string(),
  notes: z.string().optional(),
});
export type ActivationOperatorNote = z.output<typeof ActivationOperatorNote>;

/**
 * One activation in full: the summary, the handling timeline, and what it was
 * assembled from.
 *
 * `respondedDateTime` is when an operator picked it up, `completedDateTime` when
 * they finished. Both absent means nobody has attended it yet.
 */
export const ActivationDetail = ActivationSummary.extend({
  respondedDateTime: z.string().optional(),
  completedDateTime: z.string().optional(),
  /** Patriot's completion reason, e.g. `Cloud Cover/Exposure`. */
  reason: z.string().optional(),
  signals: z.array(ActivationSignal),
  operatorNotes: z.array(ActivationOperatorNote),
});
export type ActivationDetail = z.output<typeof ActivationDetail>;

export const GetActivationPathParams = z.object({ ticketNo: z.string().min(1) });
export type GetActivationPathParams = z.output<typeof GetActivationPathParams>;

/**
 * A ticket number is unique across Patriot, so the detail route needs no
 * placement in its path. What keeps one tenant out of another's activation is the
 * check that the activation's own marker names the tenant in the path prefix —
 * see the isolation note in `client-api/activations/handler.ts`.
 */
export const tenantActivationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/activations/{ticketNo}",
  scope: "tenant",
  resourcePathSchema: GetActivationPathParams,
});

export const getTenantActivationEndpoint = defineEndpoint(tenantActivationResource, {
  responseSchema: ActivationDetail,
});

/**
 * {@link ActivationDetail} plus the tenant it belongs to, as
 * {@link FleetActivationSummary}. Same allowlist, so `signals` carries only
 * the a3s-marked ones — an unattributed activation shows none.
 */
export const FleetActivationDetail = ActivationDetail.extend({
  a3sTenant: z.string().optional(),
});
export type FleetActivationDetail = z.output<typeof FleetActivationDetail>;

/**
 * Any activation in the replica by ticket, whichever tenant holds it — or none.
 *
 * Unlike the tenant detail route, there is no marker check: the fleet list
 * links here for unattributed activations and for tenants the caller's
 * directory may not hold (an int stack replicating prod's Patriot). The
 * support gate is the whole check, as for the fleet list.
 */
export const fleetActivationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/fleet/activations/{ticketNo}",
  scope: "tenant",
  resourcePathSchema: GetActivationPathParams,
});

export const getFleetActivationEndpoint = defineEndpoint(fleetActivationResource, {
  responseSchema: FleetActivationDetail,
});
